<template>
  <CustomModal
    :title="edit ? 'Update Group' : 'Add new Group'"
    v-model:visible="internalVisible"
    :width="'32vw'"
  >
    <div
      :id="`${source}_${edit ? 'edit' : 'add'}_group_modal`"
      class="flex flex-col gap-6 proxima_nova"
    >
      <div class="flex flex-row items-start gap-4">
        <div class="flex flex-col items-center gap-1 shrink-0">
          <div class="relative group cursor-pointer" @click="triggerProfilePicUpload">
            <div
              v-if="profilePicUrl"
              class="w-12 h-12 rounded-full overflow-hidden"
            >
              <img :src="profilePicUrl" alt="group avatar" class="w-full h-full object-cover" />
            </div>
            <div
              v-else
              class="w-12 h-12 rounded-full flex items-center justify-center bg_8"
            >
              <ThemeImage :icon="imageNames.GROUP" alt="group_icon" class="size-5" />
            </div>
            <div class="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
              <ThemeImage :icon="imageNames.CAMERA_GREY || imageNames.UPLOAD_GREY" alt="upload" class="size-4" />
            </div>
            <input
              ref="profilePicInput"
              type="file"
              accept="image/*"
              class="hidden"
              @change="handleProfilePicSelect"
            />
          </div>
          <span
            v-if="isUploadingProfilePic"
            class="text-[10px] secondary_color"
          >Uploading...</span>
        </div>

        <div id="name" class="flex flex-col gap-1 flex-1">
          <span class="text-[13px] font-normal secondary_color">Name</span>

          <a-input
            ref="nameInputRef"
            v-model:value="formData.name"
            class="input_medium"
            placeholder="Enter name"
            @keyup.enter="handleSubmit"
            :autofocus="true"
          />
        </div>
      </div>

      <div id="description" class="flex flex-col gap-1">
        <span class="text-[13px] font-normal secondary_color">Description</span>

        <a-textarea
          v-model:value="formData.description"
          class="input_medium !resize-none"
          placeholder="What is this group about?"
          :auto-size="{ minRows: 4, maxRows: 6 }"
        />
      </div>

      <div id="group_visibility" class="flex flex-col gap-2">
        <span class="text-[13px] font-normal secondary_color">
          Group visibility
        </span>

        <OptionRadioGroup
          :selected-value="formData.accessibility"
          :options="filteredVisibilityOptions"
          :disabled="isTypeImmutable"
          @update:selected-value="handleAccessibilityChange"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex flex-row items-center justify-end gap-2 w-full">
        <a-button
          class="secondary_medium"
          :disabled="loading"
          @click="handleCancel"
        >
          Cancel
        </a-button>

        <a-button
          class="primary_medium"
          :disabled="isActionButtonDisabled"
          :loading="loading"
          @click="handleSubmit"
        >
          {{ submitButtonText }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import CustomModal from "@/src/components/CustomModal.vue";
import OptionRadioGroup from "@/src/components/custom_components/OptionRadioGroup.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { reactive, ref, watch, computed, nextTick } from "vue";
import {
  chatGroupVisibilityOptionDetails,
  chatGroupVisibilityOptions,
} from "@/src/constants/chatConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import ChatService from "@/src/api/ChatService.js";
import DashboardService from "@/src/api/DashboardService.js";
import { message } from "ant-design-vue";
import eventBus from "@/src/utils/eventBus.js";
import store from "@/src/store/index.js";

const props = defineProps({
  source: { type: String, required: true },
  visible: { type: Boolean, default: false },
  edit: { type: Boolean, default: false },
  data: { type: Object, default: null },
  selectedUsers: { type: Array, default: () => [] },
});

const emit = defineEmits(["update:visible"]);

const loading = ref(false);
const internalVisible = ref(props.visible);
const nameInputRef = ref(null);
const profilePicInput = ref(null);
const profilePicUrl = ref(null);
const isUploadingProfilePic = ref(false);

const formData = reactive({
  name: "",
  description: "",
  topic: "",
  accessibility: chatGroupVisibilityOptions.PUBLIC,
});

const currentUserId = computed(() => store.getters["auth/userData"]?.id);

// External groups cannot change type, and non-external groups cannot become external
const isTypeImmutable = computed(() => {
  if (!props.edit || !props.data) return false;
  return props.data.type === chatGroupVisibilityOptions.EXTERNAL;
});

const filteredVisibilityOptions = computed(() => {
  if (props.edit) {
    if (props.data?.type === chatGroupVisibilityOptions.EXTERNAL) {
      // Editing external group: only show external option (locked)
      return chatGroupVisibilityOptionDetails.filter(
        o => o.value === chatGroupVisibilityOptions.EXTERNAL,
      );
    }
    // Editing non-external group: exclude external option
    return chatGroupVisibilityOptionDetails.filter(
      o => o.value !== chatGroupVisibilityOptions.EXTERNAL,
    );
  }
  return chatGroupVisibilityOptionDetails;
});

const isActionButtonDisabled = computed(() => {
  return !formData.name || !formData.name.trim() || isUploadingProfilePic.value;
});

const submitButtonText = computed(() => {
  if (loading.value) {
    return props.edit ? "Updating..." : "Creating...";
  }
  return props.edit ? "Update" : "Create";
});

const handleAccessibilityChange = (value) => {
  try {
    formData.accessibility = value;
  } catch (error) {
    console.error(
      "An error occurred while handling accessibility change: ",
      error,
    );
  }
};

const triggerProfilePicUpload = () => {
  profilePicInput.value?.click();
};

const handleProfilePicSelect = async (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith("image/")) {
    message.error("Please select an image file");
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    message.error("Image must be smaller than 5MB");
    return;
  }

  isUploadingProfilePic.value = true;
  try {
    const uploadData = new FormData();
    uploadData.append("file", file);
    const response = await DashboardService.uploadMarketingFile(uploadData);
    if (response?.success && response?.data?.url) {
      profilePicUrl.value = response.data.url;
    } else {
      message.error("Failed to upload image");
    }
  } catch (error) {
    console.error("Failed to upload profile pic:", error);
    message.error("Failed to upload image");
  } finally {
    isUploadingProfilePic.value = false;
    if (profilePicInput.value) profilePicInput.value.value = "";
  }
};

const handleCancel = () => {
  try {
    internalVisible.value = false;
    resetForm();
  } catch (error) {
    console.error("An error occurred while closing modal: ", error);
  }
};

const resetForm = () => {
  try {
    Object.assign(formData, {
      name: "",
      description: "",
      topic: "",
      accessibility: chatGroupVisibilityOptions.PUBLIC,
    });

    profilePicUrl.value = null;
    isUploadingProfilePic.value = false;
    loading.value = false;
  } catch (error) {
    console.error("An error occurred while resetting form: ", error);
  }
};

const initializeFormData = () => {
  try {
    if (props.edit && props.data) {
      formData.name = props.data.name || "";
      formData.description = props.data.description || "";
      formData.topic = props.data.topic || "";
      formData.accessibility = props.data.type || props.data.accessibility || chatGroupVisibilityOptions.PUBLIC;
      profilePicUrl.value = props.data.profile_pic || props.data.avatar_url || null;
    }
  } catch (error) {
    console.error("An error occurred while initializing form data: ", error);
  }
};

const buildPayload = () => {
  try {
    const payload = {
      name: formData.name?.trim(),
      description: formData.description?.trim(),
      topic: formData.topic?.trim(),
    };

    if (profilePicUrl.value) {
      payload.profile_pic = profilePicUrl.value;
    }

    payload.type = formData.accessibility;

    if (props.edit && props.data?.id) {
      payload.id = props.data.id;
    } else {
      // For new group creation, include members with roles
      const members = [];
      
      // Add current user as ADMIN (group creator)
      if (currentUserId.value) {
        members.push({
          user_id: currentUserId.value,
          role: "ADMIN"
        });
      }
      
      // Add selected users as MEMBER
      if (props.selectedUsers && props.selectedUsers.length > 0) {
        props.selectedUsers.forEach(user => {
          // Don't add current user again
          if (user.id !== currentUserId.value) {
            members.push({
              user_id: user.id,
              role: "MEMBER"
            });
          }
        });
      }
      
      if (members.length > 0) {
        payload.members = members;
      }
    }

    return payload;
  } catch (error) {
    console.error("An error occurred while building payload: ", error);
  }
};

const handleSubmit = async () => {
  try {
    if (props.edit) {
      await toUpdateGroup();
    } else {
      await toCreateGroup();
    }
  } catch (error) {
    console.error("An error occurred while handling submit: ", error);
  }
};

const toCreateGroup = async () => {
  try {
    loading.value = true;
    const payload = buildPayload();

    const response = await ChatService.createGroup(payload);
    if (response?.success) {
      message.success(`Group ${formData.name} created successfully!`);

      internalVisible.value = false;
      resetForm();

      if (response?.group) {
        const group = { ...response.group };
        // Ensure the group type matches what was requested
        if (payload.type) {
          group.type = payload.type;
        }

        // Register the invite token mapping for external groups
        if (group.type === "external" && group.invite_link) {
          const tokenMatch = group.invite_link.match(/\/invite\/([A-Za-z0-9_-]+)/);
          if (tokenMatch) {
            DashboardService.registerExternalInviteToken(
              tokenMatch[1],
              group.workspace_id,
              group.id,
            ).catch(() => {});
          }
        }

        eventBus.emit("group-created", group);
      } else {
        eventBus.emit("group-created");
      }
    } else {
      const errorMsg = response?.error || response?.message || "An error occurred while creating group!";
      message.error(errorMsg);
    }
  } catch (error) {
    console.error("An error occurred while creating group: ", error);
    const errorMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message || "An error occurred while creating group!";
    message.error(errorMsg);
  } finally {
    loading.value = false;
  }
};

const toUpdateGroup = async () => {
  try {
    loading.value = true;
    const payload = buildPayload();

    const response = await ChatService.updateGroup(props.data?.id, payload);
    if (response?.success) {
      message.success(`Group ${formData.name} updated successfully!`);

      internalVisible.value = false;
      resetForm();

      const activeChat = store.getters["chats/activeChat"];
      if (activeChat && String(activeChat.id) === String(props.data?.id)) {
        const updatedChat = { ...activeChat };
        if (payload.name) updatedChat.name = payload.name;
        if (payload.type) updatedChat.type = payload.type;
        if (payload.description !== undefined) updatedChat.description = payload.description;
        if (payload.topic !== undefined) updatedChat.topic = payload.topic;
        if (payload.profile_pic) updatedChat.profile_pic = payload.profile_pic;
        store.dispatch("chats/setActiveChat", updatedChat);
      }

      eventBus.emit("group-updated");
    } else {
      const errorMsg = response?.error || response?.message || "An error occurred while updating group!";
      message.error(errorMsg);
    }
  } catch (error) {
    console.error("An error occurred while updating group: ", error);
    const errorMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message || "An error occurred while updating group!";
    message.error(errorMsg);
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.visible,
  async (newVal) => {
    internalVisible.value = newVal;
    if (newVal) {
      if (props.edit) {
        initializeFormData();
      }
      await nextTick();
      nameInputRef.value?.focus();
    }
  },
);

watch(
  () => internalVisible.value,
  (newVal) => {
    emit("update:visible", newVal);
    if (!newVal) {
      resetForm();
    }
  },
);

watch(
  () => props.data,
  () => {
    if (props.edit && props.visible) {
      initializeFormData();
    }
  },
  { deep: true },
);
</script>
